<template>
  <div>
    <h4 class="mb-1">{{ $t("tests.room") }}</h4>
    <div class="body1 black20">
      {{ room_type === "" ? "-" : room_type }}
    </div>

    <h4 class="mt-4 mb-1">
      {{ $t("tests.illumination.measurement_comment") }}
    </h4>
    <div class="body1 black20">
      {{
        measurement_points_comment !== "" && measurement_points_comment !== " "
          ? measurement_points_comment
          : " - "
      }}
    </div>

    <v-divider class="mt-4"></v-divider>

    <div class="caption mt-6">{{ $t("tests.select") }}</div>
    <niko-dropdown
      class="mt-2"
      :items="allTests"
      item-text="name"
      item-value="id"
      :value="selected_test_id"
      @onSelect="(value) => setSelectedTestId(value)"
    ></niko-dropdown>

    <h4 class="mt-4">{{ $t("tests.preconditions") }}</h4>
    <div class="body1 black20 mt-1">
      {{ testRequirements }}
    </div>
    <div
      v-if="
        illumination_tests.find(
          (test) => test.illumination_test_id === selected_test_id
        )
      "
    >
      <h4 class="mt-4 mb-1">
        {{ $t("tests.illumination.measurement_conditions.label") }}
      </h4>
      <span class="body1 black20">
        {{
          selectedTestMeasurementConditions
            ? selectedTestMeasurementConditions
            : " - "
        }}
      </span>

      <div
        v-if="
          selectedTest.furniture === 'yes' || selectedTest.furniture === 'no'
        "
      >
        <h4 class="mt-4 mb-1">
          {{ $t("tests.illumination.furniture.question") }}
        </h4>
        <span class="body1 black20">
          {{ $t(`tests.illumination.furniture.${selectedTest.furniture}`) }}
        </span>
      </div>

      <points-measured-card />

      <h4>{{ $t("tests.illumination.points") }}</h4>

      <v-list disabled>
        <v-list-item-group>
          <div
            v-for="point of measurement_points.filter(
              (measurementPoint) => measurementPoint.is_active
            )"
            :key="point.measurement_point_id"
          >
            <v-list-item
              outlined
              style="height: 40px"
              class="d-flex justify-space-between"
            >
              <div>{{ point.location }}</div>
              <div class="overline">
                {{
                  getPointById(point.measurement_point_id)
                    ? `${
                        getPointById(point.measurement_point_id).measured_lux
                      } LUX`
                    : null
                }}
              </div>
            </v-list-item>
            <v-divider></v-divider>
          </div>
        </v-list-item-group>
      </v-list>
      <guidance-button page="evaluation.illumination_test" />
    </div>

    <div class="mt-10" v-else>
      <evaluator-functional-test-results
        :key="
          functional_tests.find(
            (test) => test.functional_test_id === selected_test_id
          ).functional_test_id
        "
        :test="{
          ...functional_tests.find(
            (test) => test.functional_test_id === selected_test_id
          ),
        }"
      />
    </div>

    <h2 class="bold mt-10">{{ $t("evaluation.result.title") }}</h2>
    <p class="body1 mt-4">
      {{ $t("evaluation.result.question") }}
    </p>
    <v-radio-group :disabled="state === 'completed'" v-model="is_compliant">
      <v-radio :label="$t('evaluation.result.yes')" :value="true"></v-radio>
      <v-radio :label="$t('evaluation.result.no')" :value="false"></v-radio>
    </v-radio-group>

    <div class="caption">{{ $t("evaluation.result.comment") }}</div>
    <niko-textarea
      :rows="1"
      :value="compliance_comment"
      @onChange="(value) => changeComplianceComment(value)"
      :placeholder="$t('evaluation.result.comment')"
      :disabled="state === 'completed'"
      :readonly="state === 'completed'"
    />
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from "vuex";
import NikoTextarea from "@/components/UI/NikoTextarea";
import NikoDropdown from "@/components/UI/NikoDropdown";
import GuidanceButton from "@/components/guidance/GuidanceButton";
import PointsMeasuredCard from "./PointsMeasuredCard";
import EvaluatorFunctionalTestResults from "./EvaluatorFunctionalTestResults";

export default {
  name: "evaluator-test-view",
  components: {
    NikoDropdown,
    NikoTextarea,
    PointsMeasuredCard,
    EvaluatorFunctionalTestResults,
    GuidanceButton,
  },
  computed: {
    ...mapState("projectStore", ["state"]),
    ...mapState("roomStore", [
      "room_type",
      "measurement_points_comment",
      "measurement_points",
      "illumination_tests",
      "functional_tests",
      "selected_test_id",
    ]),
    test_type() {
      if (
        this.illumination_tests.find(
          (test) => test.illumination_test_id === this.selected_test_id
        )
      ) {
        return "illumination";
      }
      if (
        this.functional_tests.find(
          (test) => test.functional_test_id === this.selected_test_id
        )
      ) {
        return "functional";
      }

      return null;
    },
    is_compliant: {
      get() {
        const test =
          this.illumination_tests.find(
            (test) => test.illumination_test_id === this.selected_test_id
          ) ||
          this.functional_tests.find(
            (test) => test.functional_test_id === this.selected_test_id
          );
        return test.is_compliant;
      },
      async set(value) {
        this.$emit("data-submitting", true);
        await this.setTestCompliance({
          test_id: this.selected_test_id,
          is_compliant: value,
          test_type: this.test_type,
          compliance_comment: this.compliance_comment,
        });
        this.$emit("data-submitting", false);
      },
    },
    compliance_comment() {
      const test =
        this.illumination_tests.find(
          (test) => test.illumination_test_id === this.selected_test_id
        ) ||
        this.functional_tests.find(
          (test) => test.functional_test_id === this.selected_test_id
        );
      return test && test.compliance_comment ? test.compliance_comment : null;
    },
    selectedTest() {
      return this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      );
    },
    selectedTestMeasurementConditions() {
      return this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      ).measurement_conditions;
    },
    allTests() {
      return [...this.illumination_tests, ...this.functional_tests].map(
        (test) => ({
          ...test,
          id: test.illumination_test_id || test.functional_test_id,
          label: `
              <div>${test.name}</div>
              <div
                class="${
                  this.isTestEvaluated(test) ? "text-green" : "text-red"
                }"
                style="font-size: 10px"
              >
                ${
                  this.isTestEvaluated(test)
                    ? this.$t("evaluation.evaluated")
                    : this.$t("evaluation.unevaluated")
                }
              </div>
            `,
        })
      );
    },
    testRequirements() {
      return this.allTests.find((test) => test.id === this.selected_test_id)
        .test_requirements !== " " &&
        this.allTests.find((test) => test.id === this.selected_test_id)
          .test_requirements !== ""
        ? `${
            this.allTests.find((test) => test.id === this.selected_test_id)
              .test_requirements
          }`
        : "-";
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "setSelectedTestId",
      "setSelectedMeasurementPointId",
    ]),
    ...mapActions("projectStore", ["setMeasurementConditions"]),
    ...mapActions("roomStore", ["setTestCompliance"]),
    async changeComplianceComment(value) {
      this.$emit("data-submitting", true);
      await this.setTestCompliance({
        test_id: this.selected_test_id,
        test_type: this.test_type,
        is_compliant: this.is_compliant,
        compliance_comment: value,
      });
      this.$emit("data-submitting", false);
    },
    getPointById(id) {
      return this.illumination_tests
        .find((test) => test.illumination_test_id === this.selected_test_id)
        .illumination_test_results.find(
          (result) => result.measurement_point_id === id
        );
    },
    isTestEvaluated(test) {
      return test.is_compliant || test.is_compliant === false;
    },
  },
};
</script>
<style lang="scss">
.evaluator-test-view {
  ::-webkit-scrollbar {
    background-color: #202324;
    color: #aba499;
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #454a4d;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: #575e62;
  }
  ::-webkit-scrollbar-thumb:active {
    background-color: #484e51;
  }
  ::-webkit-scrollbar-corner {
    background-color: #181a1b;
  }
}
</style>
